import React from 'react';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import CircularProgress from '@material-ui/core/CircularProgress';
import SectionCard from './SectionCard';
import SettingRow, {InlineSwitch} from './SettingRow';
import StatusPill from './StatusPill';
import settingsStyles from '../settingsStyles';
import {
    THIRD_PARTY_COMPONENTS_LIST,
    CONTENT_LOCATION,
} from '../../../../../constants/appUrls.constants';

/**
 * Sitede gorunen bilesenler.
 *
 * Gercek calisma sekli (api.extranetwork.com):
 *  - Harita: `cron:google-static-map` her gece 04:00'te property_contact'taki enlem/boylam
 *    ile Google Static Map gorseli uretip CDN'e yazar (zoom sabit 18). Tema, gorseli
 *    cdn.extranetwork.com/property-map/{id}/ altindan okur. Panelde acma/kapama ya da
 *    koordinat girisi yoktur — konum Konum ekranindan yonetilir.
 *  - Yorumlar: `cron:google-review` haftalik calisir; Web Bilesenleri'ndeki Google Review
 *    bileseni aktif ve placeId girilmis olmalidir. Yalnizca 4 ve 5 yildizli yorumlar
 *    kaydedilir (komutta sabit), bu yuzden "en dusuk puan" ayari yoktur.
 *    property/google-rating/get ucu hem puani okur hem saatte bir yorumlari tazeler.
 *  - WhatsApp: temada sabit konumlu (sag alt) ve yalnizca mobilde gorunur; konum ayari yoktur.
 */
export default function SiteComponentsCard({
    values = {},
    onChange,
    googleRating,
    placeId,
    showRating,
    onRefreshMap,
    mapFetching,
    onRefreshReviews,
    reviewFetching,
    onSaveNumber,
    numberDirty = {},
    settingsSaving,
    notReady,
}) {
    const {t} = useTranslation();
    const classes = settingsStyles();

    const set = (key) => (e) => {
        const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        if (typeof onChange === 'function') onChange(key, value);
    };

    // Numara alanlari yalnizca rakam kabul eder: yapistirilan "+90 (532) 111-22-33"
    // gibi degerler de aninda temizlenir. Backend zaten normalize ediyor ama
    // kullanicinin kaydedilecek degeri oldugu gibi gormesi daha anlasilir.
    const setNumber = (field) => (e) => {
        const digitsOnly = String(e.target.value || '').replace(/\D/g, '');
        if (typeof onChange === 'function') onChange(field, digitsOnly);
    };

    // Numara alani + kendi kaydet butonu. Dort yerde tekrarlandigi icin tek parca.
    //
    // Dogrulama (backend notu 01.09.2026): BOS deger gecerlidir — "tesisin kayitli
    // numarasini kullan" demektir. Dolu deger 10-15 hane olmali; kisa/uzun deger
    // servisten hata doner. Kullaniciyi bekletmemek icin ayni kural burada da
    // uygulanip kaydet butonu pasif tutuluyor.
    const numberField = (field, label, hint) => {
        const dirty = !!numberDirty[field];
        const typed = String(values[field] || '').trim();
        const invalid = typed.length > 0 && typed.length < 10;
        const canSave = dirty && !invalid && !settingsSaving;

        return (
            <>
                <div className={classes.inlineField}>
                    <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        label={label}
                        placeholder="905321112233"
                        value={values[field] || ''}
                        onChange={setNumber(field)}
                        disabled={settingsSaving}
                        error={invalid}
                        helperText={invalid
                            ? t('enw-web-settings-number_too_short', 'Ülke koduyla birlikte en az 10 hane girin.')
                            : undefined}
                        inputProps={{inputMode: 'numeric', maxLength: 15}}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && canSave) {
                                e.preventDefault();
                                onSaveNumber(field)();
                            }
                        }}
                    />
                    <Button
                        className={`${classes.btn} ${canSave ? classes.btnPrimary : ''}`}
                        onClick={onSaveNumber(field)}
                        disabled={!canSave}
                        startIcon={settingsSaving
                            ? <CircularProgress size={14} style={{color: '#fff'}} />
                            : <i className="fas fa-check" />}
                    >
                        {t('enw-web-settings-save_number', 'Numarayı Kaydet')}
                    </Button>
                </div>
                {hint && <p className={classes.hint}>{hint}</p>}
            </>
        );
    };

    const ratingScore = googleRating && googleRating.google_rating;
    const ratingTotal = googleRating && googleRating.google_rating_total;
    const reviewsReady = !!placeId;

    // Harita her zaman acik (otomatik uretiliyor), yorumlar Place ID varsa yayinda.
    // Digerleri panelden acilip kapaniyor.
    const enabledCount = [
        true,               // harita
        reviewsReady,
        values.whatsappEnabled,
        values.phoneButtonEnabled,
        values.tripleButtonsEnabled,
    ].filter(Boolean).length;

    const handleRefreshMap = () => {
        if (typeof onRefreshMap === 'function') return onRefreshMap();
        return notReady && notReady();
    };

    const handleRefreshReviews = () => {
        if (typeof onRefreshReviews === 'function') return onRefreshReviews();
        return notReady && notReady();
    };

    return (
        <SectionCard
            id="bilesenler"
            icon="fas fa-th-large"
            title={t('enw-web-settings-components_title', 'Site Bileşenleri')}
            description={t('enw-web-settings-components_desc', 'Sitenizde görünen harita, yorum ve iletişim bileşenleri')}
            meta={t('enw-web-settings-components_meta_count', {
                defaultValue: '{{count}} bileşen açık',
                count: enabledCount,
            })}
        >
            {/* --- Harita --- */}
            <SettingRow
                icon="fas fa-map-marker-alt"
                title={t('enw-web-settings-map_title', 'Harita')}
                badge={<StatusPill tone="ok" label={t('enw-web-settings-automatic', 'Otomatik')} />}
                active
                description={t('enw-web-settings-map_desc', 'Otelin konumu iletişim sayfasında harita olarak görünür. Harita görseli her gece konum bilgilerinizden otomatik üretilir.')}
                control={
                    <Button
                        className={`${classes.btn} ${classes.btnPrimary}`}
                        onClick={handleRefreshMap}
                        disabled={mapFetching}
                        startIcon={mapFetching
                            ? <CircularProgress size={14} style={{color: '#fff'}} />
                            : <i className="fas fa-sync-alt" />}
                    >
                        {t('enw-web-settings-map_refresh', 'Haritayı Yenile')}
                    </Button>
                }
            >
                <p className={classes.hint}>
                    {t('enw-web-settings-map_hint', 'Konumunuzu değiştirdiyseniz harita görselini yenileyin; enlem ve boylam Konum ekranından yönetilir.')}
                    {' '}
                    <Link className={classes.linkish} to={CONTENT_LOCATION}>
                        {t('enw-web-settings-edit_location', 'Konumu düzenle')}
                    </Link>
                </p>
            </SettingRow>

            {/* --- Misafir yorumlari --- */}
            <SettingRow
                icon="fas fa-star"
                title={t('enw-web-settings-reviews_title', 'Misafir Yorumları')}
                badge={reviewsReady
                    ? <StatusPill tone="ok" label={t('enw-web-settings-reviews_on', 'Yayında')} />
                    : <StatusPill tone="warn" label={t('enw-web-settings-reviews_no_place', 'Yer kimliği yok')} />}
                active={reviewsReady}
                description={reviewsReady
                    ? t('enw-web-settings-reviews_active_desc', 'Google işletme kaydınızdaki yorumlar sitenizde listeleniyor.')
                    : t('enw-web-settings-reviews_setup_desc', 'Yorumların görünmesi için Web Bileşenleri ekranından Google Review bileşenini açıp yer kimliğini (Place ID) girin.')}
                control={
                    reviewsReady ? (
                        <Button
                            className={`${classes.btn} ${classes.btnPrimary}`}
                            onClick={handleRefreshReviews}
                            disabled={reviewFetching}
                            startIcon={reviewFetching
                                ? <CircularProgress size={14} style={{color: '#fff'}} />
                                : <i className="fas fa-sync-alt" />}
                        >
                            {t('enw-web-settings-reviews_refresh', 'Yorumları Yenile')}
                        </Button>
                    ) : (
                        <Button
                            className={classes.btn}
                            component={Link}
                            to={THIRD_PARTY_COMPONENTS_LIST}
                            startIcon={<i className="fas fa-external-link-alt" />}
                        >
                            {t('enw-web-settings-setup', 'Tanımla')}
                        </Button>
                    )
                }
            >
                {reviewsReady && (
                    <>
                        <div className={classes.reviewSummary}>
                            {ratingScore ? (
                                <>
                                    <span className={classes.reviewScore}>{ratingScore}</span>
                                    <span className={classes.reviewStars}>
                                        {[1, 2, 3, 4, 5].map((star) => (
                                            <i
                                                key={star}
                                                className={star <= Math.round(ratingScore) ? 'fas fa-star' : 'far fa-star'}
                                            />
                                        ))}
                                    </span>
                                    {ratingTotal != null && (
                                        <span className={classes.reviewTotal}>
                                            {t('enw-web-settings-reviews_total', {
                                                defaultValue: '{{count}} yorum',
                                                count: ratingTotal,
                                            })}
                                        </span>
                                    )}
                                </>
                            ) : (
                                <span className={classes.reviewTotal}>
                                    {t('enw-web-settings-reviews_no_data', 'Puan bilgisi henüz alınmadı.')}
                                </span>
                            )}
                            <span className={classes.reviewSpacer} />
                            <StatusPill
                                tone={showRating ? 'ok' : 'off'}
                                label={showRating
                                    ? t('enw-web-settings-rating_shown', 'Puan sitede gösteriliyor')
                                    : t('enw-web-settings-rating_hidden', 'Puan gizli')}
                            />
                        </div>
                        <p className={classes.hint}>
                            {t('enw-web-settings-reviews_hint', 'Sitede yalnızca 4 ve 5 yıldızlı yorumlar listelenir; yorumlar Google’dan otomatik alınır ve elle düzenlenemez.')}
                            {' '}
                            <Link className={classes.linkish} to={THIRD_PARTY_COMPONENTS_LIST}>
                                {t('enw-web-settings-reviews_manage', 'Yer kimliği ve puan gösterimi ayarları')}
                            </Link>
                        </p>
                    </>
                )}
            </SettingRow>

            {/* --- WhatsApp --- */}
            <SettingRow
                icon="fab fa-whatsapp"
                title={t('enw-web-settings-whatsapp_title', 'WhatsApp Butonu')}
                description={t('enw-web-settings-whatsapp_desc', 'Sitenin sağ alt köşesinde sabit duran sohbet butonu. Misafir tıklayınca WhatsApp konuşması açılır. Buton yalnızca mobil cihazlarda görünür.')}
                checked={!!values.whatsappEnabled}
                onToggle={set('whatsappEnabled')}
                disabled={settingsSaving}
            >
                {numberField(
                    'whatsappNumber',
                    t('enw-web-settings-whatsapp_number', 'Telefon numarası (isteğe bağlı)'),
                    t('enw-web-settings-whatsapp_hint', 'Boş bırakırsanız tesisin cep telefonu kullanılır. Buton yalnızca mobil cihazlarda görünür ve rezervasyon adımlarında gizlenir.')
                )}
            </SettingRow>

            {/* --- Telefon butonu (myweb: onPagePhoneIcon) --- */}
            <SettingRow
                icon="fas fa-phone"
                title={t('enw-web-settings-phone_button_title', 'Telefon Butonu')}
                description={t('enw-web-settings-phone_button_desc', 'Sitenin köşesinde sabit duran arama butonu. Rezervasyon adımlarında gizlenir.')}
                checked={!!values.phoneButtonEnabled}
                onToggle={set('phoneButtonEnabled')}
                disabled={settingsSaving}
            >
                {numberField(
                    'phoneButtonNumber',
                    t('enw-web-settings-phone_button_number', 'Telefon numarası (isteğe bağlı)'),
                    t('enw-web-settings-phone_button_hint', 'Boş bırakırsanız tesisin İletişim bilgilerindeki telefon kullanılır.')
                )}
            </SettingRow>

            {/* --- Uclu yuzen butonlar (myweb: tripleFloatingButtons) --- */}
            <SettingRow
                icon="fas fa-ellipsis-v"
                title={t('enw-web-settings-triple_title', 'Üçlü Yüzen Butonlar')}
                badge={<StatusPill tone="off" label={t('enw-web-settings-home_only', 'Yalnızca ana sayfa')} />}
                description={t('enw-web-settings-triple_desc', 'Ana sayfada yan yana duran WhatsApp, yol tarifi ve arama butonları. Her biri ayrı açılıp kapatılabilir.')}
                checked={!!values.tripleButtonsEnabled}
                onToggle={set('tripleButtonsEnabled')}
                disabled={settingsSaving}
            >
                {/* WhatsApp */}
                <div className={classes.subToggleRow}>
                    <span className={classes.subToggleLabel}>
                        <i className="fab fa-whatsapp" />
                        {t('enw-web-settings-triple_whatsapp', 'WhatsApp')}
                    </span>
                    <InlineSwitch
                        checked={!!values.tripleWhatsappEnabled}
                        onChange={set('tripleWhatsappEnabled')}
                        disabled={settingsSaving}
                        label={t('enw-web-settings-triple_whatsapp', 'WhatsApp')}
                    />
                </div>
                {values.tripleWhatsappEnabled && numberField(
                    'tripleWhatsappNumber',
                    t('enw-web-settings-triple_whatsapp_number', 'WhatsApp numarası (isteğe bağlı)'),
                    t('enw-web-settings-triple_whatsapp_hint', 'Boş bırakırsanız tesisin cep telefonu kullanılır.')
                )}

                {/* Yol tarifi */}
                <div className={classes.subToggleRow}>
                    <span className={classes.subToggleLabel}>
                        <i className="fas fa-location-arrow" />
                        {t('enw-web-settings-triple_direction', 'Yol Tarifi')}
                    </span>
                    <InlineSwitch
                        checked={!!values.tripleDirectionEnabled}
                        onChange={set('tripleDirectionEnabled')}
                        disabled={settingsSaving}
                        label={t('enw-web-settings-triple_direction', 'Yol Tarifi')}
                    />
                </div>
                {values.tripleDirectionEnabled && (
                    <p className={classes.hint}>
                        {t('enw-web-settings-triple_direction_hint', 'Google Haritalar’da tesisin konumunu açar. Konum bilgisi girilmemişse buton görünmez.')}
                        {' '}
                        <Link className={classes.linkish} to={CONTENT_LOCATION}>
                            {t('enw-web-settings-edit_location', 'Konumu düzenle')}
                        </Link>
                    </p>
                )}

                {/* Telefon */}
                <div className={classes.subToggleRow}>
                    <span className={classes.subToggleLabel}>
                        <i className="fas fa-phone" />
                        {t('enw-web-settings-triple_phone', 'Arama')}
                    </span>
                    <InlineSwitch
                        checked={!!values.triplePhoneEnabled}
                        onChange={set('triplePhoneEnabled')}
                        disabled={settingsSaving}
                        label={t('enw-web-settings-triple_phone', 'Arama')}
                    />
                </div>
                {values.triplePhoneEnabled && numberField(
                    'triplePhoneNumber',
                    t('enw-web-settings-triple_phone_number', 'Telefon numarası (isteğe bağlı)'),
                    t('enw-web-settings-triple_phone_hint', 'Boş bırakırsanız tesisin İletişim bilgilerindeki telefon kullanılır.')
                )}

                <p className={classes.hint}>
                    {t('enw-web-settings-triple_footer_hint', 'Bir buton yalnızca hem açık hem de numarası/konumu tanımlıysa sitede görünür.')}
                </p>
            </SettingRow>
        </SectionCard>
    );
}
